import { Paper, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'
import { TooltipProps } from 'recharts'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { localizeEmobilityNumber } from '../utils/localize-emobility-number'
import { wallboxesSort } from '../utils/wallboxes-sort'
import { ChartDataEntry } from './ChargingSessionChart'

/**
 * Chart tooltip component for emobility charging sessions chart
 * it holds tootlip for bars above charts
 */
export const ChargingSessionChartTooltip: React.FC<
  TooltipProps<number, string> & {
    displayEnergyAll: boolean
    wallboxShades: Record<string, { grid: string; solar: string }>
    hiddenGridWallboxIds: string[]
    hiddenSolarWallboxIds: string[]
  }
> = ({ displayEnergyAll, active, payload, wallboxShades, hiddenGridWallboxIds, hiddenSolarWallboxIds }): React.ReactElement => {
  const { t } = useCustomTranslation()
  const { palette } = useTheme()

  if (!payload || payload?.length === 0 || !active) {
    return <></>
  }

  /* Payload is something that comes from recharts library
   * It is built in native Tooltip component, in TS as TooltipProps
   * Payload always comes as array, so all tooltip items must be taken from array
   * For more information, please visit https://recharts.org/en-US/api/Tooltip
   *
   * This is a "shortcut" to get the original data array which is fed to the main chart
   * and assign it to "chartData".
   *
   * Steps: Each date contains multiple bars which are vertically stacked. "Payload" is an
   * array of data values for each individual bar. Each bar data entry contains a full
   * original dataset which was fed to the chart. For simplicity, we take the original
   * "payload" from the first element in the data array.
   */

  const chartData: ChartDataEntry = payload[0].payload

  const wallboxes = Object.values(chartData.wallboxes).sort(wallboxesSort)

  const totalGridEnergy = wallboxes.reduce((totalGrid, wallbox) => parseFloat((totalGrid + wallbox.grid + wallbox.solar).toFixed(2)), 0)

  return (
    <Paper sx={theme => ({ padding: theme.spacing(2) })} className="charging-sessions-table-tooltip">
      <Stack direction="row" alignItems={'center'} rowGap={2} columnGap={2} flexWrap="wrap">
        {wallboxes
          .filter(wallbox => !hiddenGridWallboxIds.includes(wallbox.id))
          .map(wallbox => (
            <Stack
              key={'grid' + wallbox.id}
              sx={{
                borderLeft: `4px solid ${wallboxShades[wallbox.id].grid}`,
                paddingLeft: 1,
              }}
            >
              <Typography>
                {t('emobility.chargingSession.wallboxGridEnergyLabel', '', {
                  deviceName: wallbox.deviceName,
                })}
              </Typography>
              <Typography variant="caption">{t('common.format.kwh', '', { value: localizeEmobilityNumber(wallbox.grid) })}</Typography>
            </Stack>
          ))}
        {displayEnergyAll &&
          wallboxes
            .filter(wallbox => !hiddenSolarWallboxIds.includes(wallbox.id))
            .map(wallbox => (
              <Stack
                key={'solar' + wallbox.id}
                sx={{
                  borderLeft: `4px solid ${wallboxShades[wallbox.id].solar}`,
                  paddingLeft: 1,
                }}
              >
                <Typography>
                  {t('emobility.chargingSession.wallboxSolarEnergyLabel', '', {
                    deviceName: wallbox.deviceName,
                  })}
                </Typography>
                <Typography variant="caption">{t('common.format.kwh', '', { value: localizeEmobilityNumber(wallbox.solar) })}</Typography>
              </Stack>
            ))}

        <Stack sx={{ borderLeft: `1px solid ${palette.grey[500]}`, paddingLeft: 1 }}>
          <Typography>{t('emobility.chargingSession.totalEnergyCharged')}</Typography>
          <Typography variant="caption">{t('common.format.kwh', '', { value: localizeEmobilityNumber(totalGridEnergy) })}</Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}
